import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  IonButtons,
  IonModal,
  IonTitle,
  IonIcon,
} from '@ionic/react';
import { informationCircleOutline, listOutline, qrCodeOutline } from 'ionicons/icons';
import { getDoc, doc } from 'firebase/firestore';
import { useLocation, useHistory } from 'react-router-dom';

import './TrainingStart.css';
import { Machines } from '../../../datamodels';
import { getMachineImageUrl } from '../../../utils/MachineImageUtils';
import { useAuth } from '../../../auth';
import { useTimer } from '../../../contexts/TimerContext';

import {
  startWorkout,
  finishWorkout,
  cancelWorkout,
  addMachineToWorkout,
  appendSetToWorkout,
  removeSetFromWorkout,
  removeMachineFromWorkout,
} from '../../../services/WorkoutRepository';
import AddMachinesFromTheList from '../add_machines/from_list/AddMachinesFromTheList';
import ExerciseModal from '../add_machines/modal/ExerciseModal';
import TextModal from '../add_machines/modal/TextModal';

// Subcomponents
import StartEndControls from './StartEndControls';
import MachineSessionsAccordion from './MachineSessionsAccordion';

import { useTrainingStore } from '../../../stores/useTrainingStore';

import {
  requestNotificationPermission,
  showWorkoutNotification,
  clearWorkoutNotification,
} from '../../../services/BrowserNotificationService';

import { firestore } from '../../../firebase';
import { getExerciseTrackingMode } from '../../../utils/ExerciseTracking';

const StartTrainingSession: React.FC = () => {
  // -- Timer --
  const { isRunning } = useTimer();

  // -- Auth --
  const { userId } = useAuth();

  const location = useLocation();
  const history = useHistory();

  // -- Session store --
  const sessionId = useTrainingStore(s => s.sessionId);
  const startedAt = useTrainingStore(s => s.startedAt);
  const machineSessions = useTrainingStore(s => s.machineSessions);
  const setMachineSessions = useTrainingStore(s => s.setMachineSessions);
  const hydrateFromStorage = useTrainingStore(s => s.hydrateFromStorage);
  const startLocalSession = useTrainingStore(s => s.startLocalSession);
  const finishLocalSession = useTrainingStore(s => s.finishLocalSession);
  const clearLocalSession = useTrainingStore(s => s.clearLocalSession);

  const [machineImages, setMachineImages] = useState<{ [key: string]: string }>(
    {}
  );

  const [showMachinesList, setShowMachinesList] = useState(false);
  const [showStartAlert, setShowStartAlert] = useState(false);
  const [showEndAlert, setShowEndAlert] = useState(false);
  const [machineNames, setMachineNames] = useState<{ [key: string]: string }>(
    {}
  );
  const [showTextModal, setShowTextModal] = useState(false);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState<
    number | null
  >(null);
  const [selectedMachine, setSelectedMachine] = useState<Machines | null>(null);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const [showQRInfo, setShowQRInfo] = useState(false);

  // -- On mount: hydrate local session and restore notification --
  useEffect(() => {
    hydrateFromStorage();
    requestNotificationPermission();
  }, [hydrateFromStorage]);

  useEffect(() => {
    if (sessionId && startedAt) {
      showWorkoutNotification(startedAt);
    }
  }, [sessionId, startedAt]);

  // ---------- Fetch Machine Names ----------
  useEffect(() => {
    const fetchNamesAndImages = async () => {
      const newNames: { [key: string]: string } = {};
      const newImages: { [key: string]: string } = {};

      for (const sess of machineSessions) {
        const id = sess.machine_ref.id;

        // only fetch if we don't have name OR image yet
        if (!machineNames[id] || !machineImages[id]) {
          const docSnap = await getDoc(sess.machine_ref);
          if (docSnap.exists()) {
            const data: any = docSnap.data();
            if (!machineNames[id] && data?.title) newNames[id] = data.title;

            const imageUrl = getMachineImageUrl(data as Machines);
            if (!machineImages[id] && imageUrl) newImages[id] = imageUrl;
          }
        }
      }

      if (Object.keys(newNames).length) {
        setMachineNames(prev => ({ ...prev, ...newNames }));
      }
      if (Object.keys(newImages).length) {
        setMachineImages(prev => ({ ...prev, ...newImages }));
      }
    };

    if (machineSessions.length) fetchNamesAndImages();
  }, [machineSessions, machineNames, machineImages]);

  // ---------- Start / End Logic ----------
  const handleStartTraining = async () => {
    if (!userId) return;

    const workout = await startWorkout(userId);
    if (!workout) return;

    startLocalSession(workout.sessionId, workout.startedAt);
    await showWorkoutNotification(workout.startedAt);
    setShowStartAlert(false);
  };

  // Auto-start on navigation from Training page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const autostart = params.get('autostart') === '1';

    if (!autostart) return;
    if (!userId) return;

    // If already running or session exists, do nothing
    if (isRunning || sessionId) {
      // Clean query param so refresh doesn't re-trigger intent forever
      history.replace('/my/trainingstart');
      return;
    }

    // Start immediately
    handleStartTraining();

    // Clean query param after starting
    history.replace('/my/trainingstart');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, userId]);


  // Add a machine selected from the general machine list (e.g. via 3D advisor)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const machineId = params.get('addMachine');

    if (!machineId || !sessionId) return;

    const addSelectedMachine = async () => {
      const snap = await getDoc(doc(firestore, 'machines', machineId));
      if (snap.exists()) {
        handleMachineSelection({ id: snap.id, ...snap.data() } as Machines);
      }
      history.replace('/my/trainingstart');
    };

    addSelectedMachine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, sessionId]);

  // Called by the IonAlert in StartEndControls
  const handleEndTraining = () => {
    if (!sessionId) return console.error('No session to end');
    setShowEndAlert(true);
  };

  // Simplified confirmEndTraining without Routine logic
  const confirmEndTraining = async (shouldEnd: boolean, isCancel = false) => {
    if (!sessionId) {
      setShowEndAlert(false);
      return console.error('Session ID is null.');
    }

    if (isCancel) {
      // user selected "Cancel Session"
      cancelWorkout(
        sessionId,
        () => {
          clearLocalSession();
          clearWorkoutNotification();
          history.replace('/my/training');
        },
        err => console.error(err)
      );
    } else if (shouldEnd) {
      // Wait until the completed session exists in Firestore so the Training
      // screen can derive Ploppy's state from the real session list.
      await finishWorkout(sessionId, machineSessions, setMachineSessions);
      finishLocalSession();
      clearWorkoutNotification();
      history.replace('/my/training');
    }

    setShowEndAlert(false);
  };

  // ---------- Machine + QR Logic (unchanged) ----------
  const handleMachineSelection = async (machine: Machines) => {
    if (!sessionId) return;
    if (machine.exercises?.length === 1) {
      setSelectedMachine(machine);
      setSelectedExercise(machine.exercises[0].name);
      setShowTextModal(true);
    } else {
      // Otherwise, open the exercise modal
      setSelectedMachine(machine);
      setShowExerciseModal(true);
    }
    setShowMachinesList(false);
  };

  const handleExerciseSelection = (name: string) => {
    setSelectedExercise(name);
    setShowExerciseModal(false);
    setShowTextModal(true);
  };

  // ---------- Modals for Adding Sets ----------
  const handleTextModalConfirm = async (reps: number, weight: number) => {
    if (selectedMachine && sessionId) {
      await addMachineToWorkout(
        userId!,
        sessionId,
        selectedMachine,
        selectedExercise!,
        reps,
        weight,
        machineSessions,
        setMachineSessions
      );
      setSelectedMachine(null);
    }
    setShowTextModal(false);
  };

  const handleAddSetToSession = async (reps: number, weight: number) => {
    if (selectedSessionIndex !== null && sessionId) {
      const updated = await appendSetToWorkout(
        machineSessions,
        selectedSessionIndex,
        reps,
        weight
      );
      setMachineSessions(updated);
    }
    setShowTextModal(false);
    setSelectedSessionIndex(null);
  };

  const handleRemoveSet = async (si: number, wi: number) => {
    const updated = await removeSetFromWorkout(machineSessions, si, wi);
    setMachineSessions(updated);
  };

  const entrySession = selectedSessionIndex !== null ? machineSessions[selectedSessionIndex] : undefined;
  const entryMachineTitle = entrySession
    ? machineNames[entrySession.machine_ref.id]
    : selectedMachine?.title;
  const entryExerciseName = entrySession?.exercise_name || selectedExercise || undefined;
  const entryExercise = selectedMachine?.exercises?.find(
    exercise => exercise.name.trim().toLocaleLowerCase() === entryExerciseName?.trim().toLocaleLowerCase()
  );
  const entryTrackingMode = getExerciseTrackingMode(entryMachineTitle, entryExercise || (entryExerciseName ? { name: entryExerciseName } : undefined));

  // ---------- UI ----------
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar />
      </IonHeader>

      <IonContent className="ion-padding trainingStart">
        {/* Start/End Controls + Alerts */}
        <StartEndControls
          isRunning={isRunning}
          showStartAlert={showStartAlert}
          setShowStartAlert={setShowStartAlert}
          showEndAlert={showEndAlert}
          setShowEndAlert={setShowEndAlert}
          handleStartTraining={handleStartTraining}
          handleEndTraining={handleEndTraining}
          confirmEndTraining={confirmEndTraining}
        />

        {/* Add Machines quick-card */}
        {isRunning && (
          <div className="quickCard">
            <div className="quickCardTitle">Add machines</div>

            <div className="quickCardActions">
              <IonButton
                className="quickCardBtn"
                expand="block"
                onClick={() => setShowMachinesList(true)}
              >
                <IonIcon icon={listOutline} slot="start" />
                From list
              </IonButton>
              <IonButton className="quickCardBtn quickCardBtn--qr" expand="block" fill="outline" onClick={() => setShowQRInfo(true)}>
                <IonIcon icon={qrCodeOutline} slot="start" />
                From QR
                <IonIcon icon={informationCircleOutline} slot="end" />
              </IonButton>
            </div>
          </div>
        )}

        {/* Machines List in a Modal */}
        <IonModal
          isOpen={showMachinesList}
          onDidDismiss={() => setShowMachinesList(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Select Machine</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowMachinesList(false)}>
                  Close
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <AddMachinesFromTheList onSelectMachine={handleMachineSelection} />
          </IonContent>
        </IonModal>

        <IonModal isOpen={showQRInfo} onDidDismiss={() => setShowQRInfo(false)} initialBreakpoint={0.62} breakpoints={[0, 0.62, 0.9]} className="qrInfoModal">
          <IonHeader><IonToolbar><IonTitle>QR machine scan</IonTitle><IonButtons slot="end"><IonButton onClick={() => setShowQRInfo(false)}>Close</IonButton></IonButtons></IonToolbar></IonHeader>
          <IonContent className="qrInfoContent">
            <div className="qrInfoHero">
              <div className="qrInfoHero__icon"><IonIcon icon={qrCodeOutline} /><span><IonIcon icon={informationCircleOutline} /></span></div>
              <div><span>Working feature</span><h2>Scan. Add. Start your set.</h2></div>
            </div>
            <p>QR scanning already works in PawerLifting. It can add the correct machine to your active workout in seconds.</p>
            <div className="qrInfoSupport"><strong>Why is scanning paused?</strong><p>For the feature to work in a real gym, its machines need PawerLifting QR labels connected to the catalogue. That needs support and cooperation from gyms.</p></div>
            <p className="qrInfoCallout">Would you use this feature? Please let me know — and tell your gym about PawerLifting. Your interest can help bring QR scanning to your machines.</p>
            <IonButton expand="block" shape="round" onClick={() => setShowQRInfo(false)}>Got it</IonButton>
          </IonContent>
        </IonModal>

        {/* Machine Sessions Accordion */}
        <div className="accordionCard">
          <MachineSessionsAccordion
            machineSessions={machineSessions}
            machineNames={machineNames}
            machineImages={machineImages}
            sessionId={sessionId!}
            handleRemoveSet={handleRemoveSet}
            setSelectedSessionIndex={setSelectedSessionIndex}
            setShowTextModal={setShowTextModal}
            deleteMachineSession={(machineSessionId, activeSessionId, sessions, setter) => {
              if (!activeSessionId) return Promise.resolve();
              return removeMachineFromWorkout(
                machineSessionId,
                activeSessionId,
                sessions,
                setter
              );
            }}
            setMachineSessions={setMachineSessions}
          />
        </div>

        {/* Exercise Modal */}
        <ExerciseModal
          isOpen={showExerciseModal}
          machineTitle={selectedMachine?.title}
          exercises={selectedMachine?.exercises || []}
          onConfirm={handleExerciseSelection}
          onCancel={() => setShowExerciseModal(false)}
        />

        {/* Reps/Weight Modal */}
        <TextModal
          isOpen={showTextModal}
          trackingMode={entryTrackingMode}
          onConfirm={
            selectedSessionIndex !== null
              ? handleAddSetToSession
              : handleTextModalConfirm
          }
          onCancel={() => {
            setShowTextModal(false);
            setSelectedSessionIndex(null);
          }}
        />
      </IonContent>
    </IonPage>
  );
};

export default StartTrainingSession;
